import { useNavigate } from 'react-router-dom';
import { useEffect, useMemo, useRef, useState } from 'react';
import { io } from 'socket.io-client';
import { BsLightningChargeFill, BsLeafFill } from 'react-icons/bs';
import { MdSolarPower } from 'react-icons/md';
import PhasePin from '../components/PhasePin';
import AlertsList from '../components/AlertsList';
import '../App.css';

const formatPower = (value) => `${value.toFixed(1)} MW`;
const format = (val, unit) => {
  const u = unit || '';
  return u ? `${val} ${u}` : `${val}`;
};

const getPhaseTransform = (phase) => {
  const topValue = Number.parseFloat(phase.position.top);
  if (Number.isNaN(topValue)) return 'translate(-50%, -110%)';
  if (topValue < 40) return 'translate(-50%, -28%)';
  if (topValue > 40) return 'translate(-50%, -120%)';
  return 'translate(-50%, -80%)';
};

const clampPercent = (value, min = 15, max = 85) => {
  if (Number.isNaN(value)) return min;
  return Math.min(Math.max(value, min), max);
};

const formatTimestamp = (dateObj) => {
  if (!dateObj) return '-';
  const pad = (n) => String(n).padStart(2, '0');
  const yyyy = dateObj.getFullYear();
  const mm = pad(dateObj.getMonth() + 1);
  const dd = pad(dateObj.getDate());
  const hh = pad(dateObj.getHours());
  const mi = pad(dateObj.getMinutes());
  const ss = pad(dateObj.getSeconds());
  return `${yyyy}-${mm}-${dd} ${hh}:${mi}:${ss}`;
};

const formatReadableTimestamp = (value) => {
  if (!value) return '-';
  if (value instanceof Date) return formatTimestamp(value);
  if (typeof value === 'string') {
    const trimmed = value.trim();
    const isoLike = trimmed.match(/^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2}:\d{2})(?:\.\d+)?Z?$/);
    if (isoLike) return `${isoLike[1]} ${isoLike[2]}`;
    const compact = trimmed.match(/^(\d{4})(\d{2})(\d{2})(\d{2})(\d{2})(\d{2})$/);
    if (compact) return `${compact[1]}-${compact[2]}-${compact[3]} ${compact[4]}:${compact[5]}:${compact[6]}`;
    return trimmed.replace('T', ' ').replace(/Z$/, '');
  }
  const parsed = new Date(value);
  if (!Number.isNaN(parsed.getTime())) return formatTimestamp(parsed);
  return `${value}`;
};

const METRICS_TIMESTAMP_KEY = 'inokom_metrics_last_updated';
const METRICS_SIGNATURE_KEY = 'inokom_metrics_signature';

const getMetricsSignature = (data) => JSON.stringify(data || {});

const getStoredMetricsTimestamp = () => {
  const raw = sessionStorage.getItem(METRICS_TIMESTAMP_KEY);
  if (!raw) return null;
  const parsed = new Date(raw);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const storeMetricsTimestamp = (dateObj) => {
  if (!dateObj) return;
  sessionStorage.setItem(METRICS_TIMESTAMP_KEY, dateObj.toISOString());
};

//const USE_STATIC_METRICS = true;  flip to false to resume live data
// const STATIC_OVERRIDES = {
//   phase1Power: '816.6 kW',
//   phase2Power: '4390 kW',
//   overallPower: '5206.6 kW',
//   totalEnergy: '1.43 MWh',
//   totalCo2: '1417.65 ton',
// };

// const applyOverrides = (data) => {
//   const entries = Object.entries(STATIC_OVERRIDES).filter(([, v]) => v !== null && v !== undefined);
//   return entries.length ? { ...data, ...Object.fromEntries(entries) } : data;
// };

export default function MainPage({ phases, layoutImage, onLogout }) {
  const [activePhase, setActivePhase] = useState(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [isAlertOpen, setIsAlertOpen] = useState(false); 
  const [metrics, setMetrics] = useState({
    phase1Power: 0,
    phase2Power: 0,
    overallPower: 0,
    totalEnergy: 0,
    totalCo2: 0,
  });
  const [lastUpdated, setLastUpdated] = useState(() => getStoredMetricsTimestamp());
  const [inverterSummary, setInverterSummary] = useState([]);
  const [faultAlerts, setFaultAlerts] = useState([]);
  const layoutRef = useRef(null);
  const alertBarRef = useRef(null);
  const navigate = useNavigate();
  const badgeInokom = encodeURI(`${process.env.PUBLIC_URL}/Inokom_Logo.png`);
  const badgeSdes = encodeURI(`${process.env.PUBLIC_URL}/sdes_logo.png`);
  //const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:5200';
  const API_BASE = '/inokom/api';
  const socketUrl = '/inokom/ws';
  const socket = useMemo(() => io({path: `${socketUrl}/socket.io`, transports: ['websocket', 'polling'] }), []);

  const applyMetricsUpdate = (data) => {
    const nextSignature = getMetricsSignature(data);
    const previousSignature = sessionStorage.getItem(METRICS_SIGNATURE_KEY);

    setMetrics(data);

    if (previousSignature !== nextSignature) {
      const now = new Date();
      setLastUpdated(now);
      sessionStorage.setItem(METRICS_SIGNATURE_KEY, nextSignature);
      storeMetricsTimestamp(now);
      return;
    }

    const storedTimestamp = getStoredMetricsTimestamp();
    if (storedTimestamp) setLastUpdated(storedTimestamp);
  };

  useEffect(() => {
    // initial metrics fetch
    fetch(`${API_BASE}/metrics`)
      .then((r) => r.json())
      .then((data) => {
        console.log('[metrics][http]', data);
        applyMetricsUpdate(data);
	// setMetrics(applyOverrides(data));
      })
      .catch((err) => console.error('[metrics][http][error]', err));

    // initial inverter summary fetch
    fetch(`${API_BASE}/inverters/summary`)
      .then((r) => r.json())
      .then((data) => {
        setInverterSummary(data || []);
      })
      .catch((err) => console.error('[inverters][http][error]', err));
  }, [API_BASE]);

  useEffect(() => {
    const handler = (data) => {
      // console.log('[metrics][ws]', data);
      applyMetricsUpdate(data);
      // setMetrics(applyOverrides(data));
    };
    const invHandler = (data) => {
      setInverterSummary(data || []);
    };
    const faultHandler = (data) => {
      setFaultAlerts(Array.isArray(data) ? data : []);
    };
    socket.on('metrics', handler);
    socket.on('inverterSummary', invHandler);
    socket.on('faultAlerts', faultHandler);
    socket.emit('faultAlerts:request', {});
    return () => {
      socket.off('metrics', handler);
      socket.off('inverterSummary', invHandler);
      socket.off('faultAlerts', faultHandler);
      socket.disconnect();
    };
  }, [socket]);

  useEffect(() => {
    socket.emit('faultAlerts:request', activePhase ? {
      phase_id: getPhaseBackendId(activePhase),
      ps_name: getPhaseDataName(activePhase),
    } : {});
  }, [socket, activePhase]);

  const handleCanvasClick = (event) => {
    if (!layoutRef.current) return;
    const rect = layoutRef.current.getBoundingClientRect();
    const topPct = ((event.clientY - rect.top) / rect.height) * 100;
    const leftPct = ((event.clientX - rect.left) / rect.width) * 100;
    // console.info('Phase position example:', {
    //    top: `${topPct.toFixed(1)}%`,
    //    left: `${leftPct.toFixed(1)}%`,
    // });
  };

  const visibleAlerts = activePhase
    ? faultAlerts.filter((alert) => alert.phase_id === getPhaseBackendId(activePhase) || alert.ps_name === getPhaseDataName(activePhase))
    : faultAlerts;
  const alertToggleBottom = isAlertOpen ? `${(alertBarRef.current?.offsetHeight || 0) - 2}px` : '6px';

  return (
    <div className="dashboard">
      <div className="dashboard-layer">
        <button className="logout-button" onClick={onLogout}>
          Logout
        </button>
        <div className="phase-legend glass">
          <div className="legend-item">
            <span className="legend-swatch phase1" />
            <span className="legend-label">Phase 1</span>
          </div>
          <div className="legend-item">
            <span className="legend-swatch phase2" />
            <span className="legend-label">Phase 2</span>
          </div>
        </div>
        <div className="brand-badge glass">
          <div className="brand-logos">
            <img src={badgeInokom} alt="Inokom logo" className="brand-img" />
            <img src={badgeSdes} alt="SDES logo" className="brand-img" />
          </div>
        </div>
        <div className={`summary-card glass ${isSidebarOpen ? 'open' : 'collapsed'}`}>
          <button
            className="sidebar-toggle"
            onClick={(event) => {
              event.stopPropagation();
              setIsSidebarOpen((open) => !open);
            }}
          >
            {isSidebarOpen ? '<' : '>'}
          </button>
          <div className="summary-card__content">
            <div className="card-header">
              <div>
                <h2>Inokom Solar Generation</h2>
                <p className="summary-updated">
                  Updated at: {formatTimestamp(lastUpdated)}
                </p>
              </div>
            </div>
            <div className="metrics-grid">
              <Metric label="Phase 1 Generation" value={metrics.phase1Power} />
              <Metric label="Phase 2 Generation" value={metrics.phase2Power} />
	            <Metric label="Overall Generation" value={metrics.overallPower} />
              <Metric label="Total Solar Generation" value={metrics.totalEnergy} />
              <Metric label="Total CO2 Reduction to date" value={metrics.totalCo2} />
            </div>
          </div>
        </div>

        <div
          className="layout-canvas"
          ref={layoutRef}
          style={{
            backgroundImage: `linear-gradient(125deg, rgba(4, 17, 35, 0.35), rgba(4, 17, 35, 0.2)), url(${layoutImage})`,
            backgroundSize: '100% 95%',
            // backgroundPositionX: '60%',
            // backgroundPositionY: '60%',
            backgroundRepeat: 'no-repeat',
          }}
          onClick={handleCanvasClick}
        >
          {phases.map((phase) => {
            const summary = findSummaryForPhase(phase, inverterSummary);
            const displayPhase = { ...phase, summary };
            return <PhasePin key={phase.phaseId} phase={displayPhase} onSelect={setActivePhase} />;
          })}
        </div>
      </div>

          {activePhase ? (
            <div
          className="phase-detail glass"
          style={{
            top: activePhase.position.top,
            left: `${clampPercent(Number.parseFloat(activePhase.position.left))}%`,
            transform: getPhaseTransform(activePhase),
            marginTop: '8%',
            marginBottom: '15%',
          }}
        >
          <div className="phase-detail__header">
            <div className="phase-detail__title">
              <p className="eyebrow" style={{ color: '#171717', fontWeight: 'bold', fontSize: '14px' }}>
                {getPhaseDisplayName(activePhase)}
              </p>
            </div>
            <button
              className="icon-button phase-detail__close"
              onClick={() => setActivePhase(null)}
              aria-label="Close"
            >
              x
            </button>
          </div>
          {activePhase.summary ? (
            <>
              <h3>{formatPowerFromW(activePhase.summary.total_power)}</h3>
              <p className="muted" style={{fontStyle:'inherit', color:'#191a1b', fontSize:'16px'}}>
                Last updated: {formatReadableTimestamp(activePhase.summary.updated_at)}
              </p>
              <div className="building-list">
                <div className="building-row">
                  <span>Total Inverters</span>
                  <span>{activePhase.summary.inverter_count}</span>
                </div>
                <div className="building-row">
                  <span>Status</span>
                  <span>
                    {activePhase.summary.online_count ?? '-'} (online) / {activePhase.summary.offline_count ?? '-'} (offline)
                  </span>
                </div>
              </div>
              <div style={{ marginTop: 12, display: 'flex', justifyContent: 'flex-end' }}>
                <button className="primary-button" onClick={() => navigate(`/analytics/${activePhase.phaseId}`)}>
                  See more
                </button>
              </div>
            </>
          ) : null}
        </div>
      ) : null}

      <button
        className={`alert-toggle ${isAlertOpen ? 'open' : ''}`}
        style={{ bottom: alertToggleBottom }}
        onClick={() => setIsAlertOpen((open) => !open)}
      >
        {isAlertOpen ? 'Hide alerts' : 'Show alerts'}
      </button>
      <div ref={alertBarRef} className={`alert-bar ${isAlertOpen ? 'open' : ''}`}>
        <div className="alert-content">
          <p className="eyebrow">Alerts {activePhase ? `- ${getPhaseDisplayName(activePhase)}` : ''}</p>
          <AlertsList alerts={visibleAlerts.slice(0, 6)} />
        </div>
      </div>
    </div>
  );
}

function Metric({ label, value, detail }) {
  const display = label.toLowerCase().includes('co2')
    ? formatCo2ToTons(value)
    : formatNumberWithCommas(value);
  const metricMeta = getMetricMeta(label);
  return (
    <div className={`metric metric--${metricMeta.tone}`}>
      <div className="metric-icon" aria-hidden="true">
        <MetricIcon tone={metricMeta.tone} />
      </div>
      <div className="metric-copy">
        <p className="metric-label">{label}</p>
        <p className="metric-value">{display}</p>
        {detail ? <p className="metric-detail">{detail}</p> : null}
      </div>
    </div>
  );
}

function getMetricMeta(label) {
  const normalized = `${label}`.toLowerCase();
  if (normalized.includes('phase 1')) {
    return { tone: 'phase1' };
  }
  if (normalized.includes('phase 2')) {
    return { tone: 'phase2' };
  }
  if (normalized.includes('overall')) {
    return { tone: 'overall' };
  }
  if (normalized.includes('solar')) {
    return { tone: 'solar' };
  }
  if (normalized.includes('co2')) {
    return { tone: 'co2' };
  }
  return { tone: 'default' };
}

function MetricIcon({ tone }) {
  if (tone === 'solar') {
    return <MdSolarPower className="metric-icon-svg" />;
  }

  if (tone === 'co2') {
    return <BsLeafFill className="metric-icon-svg" />;
  }

  return <BsLightningChargeFill className="metric-icon-svg" />;
}

function formatNumberWithCommas(val) {
  if (val == null) return val;
  // if it's already a string with units (e.g., "123 kW"), split and format numeric part
  if (typeof val === 'string') {
    // const match = val.match(/^(-?\\d+(?:\\.\\d+)?)(.*)$/);
    const cleaned = val.replace(/,/g, '');
    const match = cleaned.match(/^(-?\d+(?:\.\d+)?)(.*)$/);
    if (match) {
      const [, num, rest] = match;
      const formatted = Number(num).toLocaleString(undefined, { maximumFractionDigits: 3 });
      return `${formatted}${rest}`;
    }
    return val;
  }
  if (typeof val === 'number') {
    return val.toLocaleString(undefined, { maximumFractionDigits: val % 1 === 0 ? 0 : 3 });
  }
  return val;
}

function formatCo2ToTons(val) {
  if (val == null) return val;
  let num = null;
  let unit = 'ton';
  if (typeof val === 'string') {
    // const match = val.match(/^(-?\d+(?:\.\d+)?)(.*)$/);
    const cleaned = val.replace(/,/g, '');
    const match = cleaned.match(/^(-?\d+(?:\.\d+)?)(.*)$/);
    if (match) {
      num = Number(match[1]);
      const rest = match[2].toLowerCase();
      if (rest.includes('kg')) {
        num = num / 1000;
      }
    }
  } else if (typeof val === 'number') {
    num = val / 1000; // assume kg if raw number
  }
  if (num == null || Number.isNaN(num)) return val;
  return `${num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${unit}`;
}

const formatPowerFromW = (value) => {
  const num = Number(value) || 0;
  if (num >= 1_000_000) return `${(num / 1_000_000).toFixed(2)} MW`;
  if (num >= 1_000) return `${(num / 1000).toFixed(2)} kW`;
  return `${num.toFixed(0)} W`;
};

const findSummaryForPhase = (phase, list) => {
  if (!Array.isArray(list)) return null;
  const target = getPhaseDataName(phase).toLowerCase().trim();
  return list.find((s) => s.phase_id === getPhaseBackendId(phase) || (s.ps_name || '').toLowerCase().trim() === target) || null;
};

const getPhaseBackendId = (phase) => phase?.phaseId || '';
const getPhaseDataName = (phase) => phase?.name || '';

const getPhaseDisplayName = (phase) => phase?.displayName || phase?.name || '';
